/**
 * Browser-side pre-checks for image uploads (node logo, entity pictures).
 * Mirrors the hard limits in apps/backend/app/services/images.py. These are
 * a courtesy — instant feedback, no round trip — and the backend re-checks
 * the bytes regardless.
 */

export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
export const MAX_IMAGE_DIMENSION = 2048;
export const MIN_IMAGE_DIMENSION = 16;

// Resolve {width, height} of an image File without uploading it, or null if
// the browser can't decode it (the backend will then say why).
export function readImageDimensions(file) {
    return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
}

/**
 * Throw an Error with a user-facing message if `file` fails a pre-check;
 * resolve otherwise. `what` names the thing in messages ("Logo", "Picture").
 */
export async function precheckImage(file, what = 'Image') {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error(`${what} must be a PNG, JPEG, or WEBP image`);
    }
    if (file.size > MAX_IMAGE_BYTES) {
        throw new Error(`${what} must be under ${MAX_IMAGE_BYTES / (1024 * 1024)}MB`);
    }
    const dims = await readImageDimensions(file);
    if (dims && (dims.width > MAX_IMAGE_DIMENSION || dims.height > MAX_IMAGE_DIMENSION)) {
        throw new Error(`${what} must be at most ${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION} pixels`);
    }
    if (dims && (dims.width < MIN_IMAGE_DIMENSION || dims.height < MIN_IMAGE_DIMENSION)) {
        throw new Error(`${what} must be at least ${MIN_IMAGE_DIMENSION}x${MIN_IMAGE_DIMENSION} pixels`);
    }
}
